/*
 * BioIndex — Android push notification bridge.
 * Runs only in the native Android app; web and desktop React behavior is untouched.
 * The existing NotificationBadgeContext remains the source of truth for unread counts.
 */
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { getFunctions, httpsCallable } from "firebase/functions";

const CHANNEL_ID = "bioindex_updates";
// Serialize register/unregister while React changes accounts or remounts.
let nativeRegistrationQueue = Promise.resolve();
function queueNativeRegistration(task) {
  const next = nativeRegistrationQueue.catch(() => {}).then(task);
  nativeRegistrationQueue = next.catch(() => {});
  return next;
}
const ALLOWED_DESTINATIONS = new Set([
  "/community?view=notifications",
  "/community?view=community",
]);

function nativeAndroid() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

function openNotificationDestination(action) {
  const requested = String(action?.notification?.data?.route || "");
  const destination = ALLOWED_DESTINATIONS.has(requested)
    ? requested
    : "/community?view=notifications";
  window.location.assign(destination);
}

export default function NativePushBridge({ uid, enabled, loaded, total }) {
  useEffect(() => {
    if (!nativeAndroid() || !enabled || !uid) return undefined;

    let cancelled = false;
    let currentToken = "";
    let handles = [];
    const functions = getFunctions(undefined, "southamerica-east1");
    const registerDevice = httpsCallable(functions, "registerBioIndexPushDevice");
    const unregisterDevice = httpsCallable(functions, "unregisterBioIndexPushDevice");

    async function start() {
      try {
        // Add listeners BEFORE register(), otherwise the token event may be lost.
        const newHandles = await Promise.all([
          PushNotifications.addListener("registration", async ({ value }) => {
            if (cancelled || !value) return;
            currentToken = value;
            try {
              await registerDevice({ token: value });
            } catch (error) {
              console.error("BioIndex Android push token could not be saved:", error);
            }
          }),
          PushNotifications.addListener("registrationError", (error) => {
            console.error("BioIndex Android push registration failed:", error);
          }),
          PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            if (!cancelled) openNotificationDestination(action);
          }),
        ]);
        handles = newHandles;
        if (cancelled) {
          await Promise.all(newHandles.map((handle) => handle.remove()));
          return;
        }

        await PushNotifications.createChannel({
          id: CHANNEL_ID,
          name: "BioIndex Updates",
          description: "Community activity and personal notifications",
          importance: 3,
          visibility: 1,
          showBadge: true,
        });

        let permission = await PushNotifications.checkPermissions();
        if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
          permission = await PushNotifications.requestPermissions();
        }
        if (!cancelled && permission.receive === "granted") {
          await queueNativeRegistration(() => cancelled
            ? Promise.resolve()
            : PushNotifications.register());
        }
      } catch (error) {
        console.error("BioIndex Android push setup failed:", error);
      }
    }

    start();
    return () => {
      cancelled = true;
      handles.forEach((handle) => handle.remove());
      // On account changes or sign-out, revoke the local FCM registration.
      // This avoids delivering activity belonging to an earlier account.
      // Queue native revocation immediately, BEFORE the next account registers.
      // Firestore cleanup can happen independently and may fail after sign-out.
      queueNativeRegistration(() => PushNotifications.unregister()).catch((error) => {
        console.warn("BioIndex Android push unregister failed:", error);
      });
      if (currentToken) {
        unregisterDevice({ token: currentToken }).catch(() => {
          // The native token has already been revoked even if Firebase Auth expired.
        });
      }
    };
  }, [uid, enabled]);

  useEffect(() => {
    if (!nativeAndroid() || !enabled || !uid || !loaded || total !== 0) return;
    // Clear native BioIndex notifications when every in-app badge is resolved.
    // This also clears the Samsung launcher badge when Android owns that badge.
    PushNotifications.removeAllDeliveredNotifications().catch((error) => {
      console.warn("Could not clear resolved Android notifications:", error);
    });
  }, [uid, enabled, loaded, total]);

  return null;
}
